import { wrapText, calculateOptimalFontSize } from './calculateFontSize';

export interface ResolveTextLayoutInput {
  text: string;
  /** Per-account size in PREVIEW/template space (px). Nullish/<=0 → auto-fit. */
  fontSize: number | null | undefined;
  /** Render-space scale (1080 / previewWidth). */
  scale: number;
  maxWidth: number;
  maxHeight: number;
  fontFamily: string;
  ctx: CanvasRenderingContext2D;
  /** Seed size for auto-fit when no explicit size; defaults to 20 * scale. */
  autoFitSeed?: number;
}

/**
 * Decide the rendered font size + wrapped lines for a text block.
 *
 * When the editor set an explicit per-account `fontSize` (>0), it is honored
 * EXACTLY (scaled to render space) — no auto-shrink/grow — so the published
 * output matches the preview 1:1. Editors rely on exact per-account sizing.
 * Only when no size is set do we auto-fit the text into the box.
 */
export function resolveTextLayout(input: ResolveTextLayoutInput): { fontSize: number; lines: string[] } {
  const { text, fontSize, scale, maxWidth, maxHeight, fontFamily, ctx } = input;
  if (fontSize && fontSize > 0) {
    const exact = fontSize * scale;
    return { fontSize: exact, lines: wrapText(text, maxWidth, exact, fontFamily, ctx) };
  }
  const seed = input.autoFitSeed ?? 20 * scale;
  return calculateOptimalFontSize(text, maxWidth, maxHeight, fontFamily, ctx, seed);
}
